// The recorder engine's middle layer (recorderCore.ts below it, the capture
// half recorderEngine.ts above): taking over what a closed page of this
// sign-in left on the device and asking "Carry on?" until it finishes on its
// own; keeping a take Corvus found no audio in while this device still has
// some (I1); a mute long enough to matter, as a stretch not recorded; the
// Mac app's flush (its slice written before a quit: `flushed`, I3); and a
// call recording's two sides (callSound.ts).

import { micErrorMessage } from "./micWords";
import { callStartError } from "./sourceModel";
import { FLUSH_WAIT_MS, MUTE_GAP_MS, OWNER_STALE_MS, autoFinishAt } from "./recorderModel";
import { RecorderCore, filed, iso, lockName } from "./recorderCore";
import type { TakeRecord } from "./recorderStore";
import { newRuntime, type Runtime, type StreamLike } from "./recorderTypes";

export abstract class RecorderTakes extends RecorderCore {
  /** Flushes waiting for a take's next piece. */
  private pieceWaiters = new Set<{ rt: Runtime; fn: () => void }>();

  /**
   * Take over what a closed page of this sign-in left on the device (another
   * person's takes are left for them; they still count in `stored`). Every
   * unfinished take of this sign-in taken over: `adopted` (the Mac app's
   * `bound`); one another live page still holds, or a store that cannot be
   * listed, or memory standing in for a device store that did not open in
   * time (its takes not seen): not yet (the next look, every minute, tries
   * again, IndexedDB too).
   */
  protected async adoptStored(): Promise<void> {
    await this.reopenUnopenedStore();
    const store = await this.ensureStore();
    const { clock, locks, tabId } = this.deps;
    const who = `${this.customerId}\u0000${this.owner}`;
    let takes: TakeRecord[] = [];
    try {
      takes = await store.listTakes();
    } catch {
      this.adopted = false;
      this.changed();
      return;
    }
    this.listed = new Map(
      takes.filter((t) => this.owns(t) && t.status !== "done" && t.status !== "kept").map((t) => [t.id, { filed: filed(t), meetingId: t.meetingId }]),
    );
    // Every take is taken over before any is sent: a carry-on listed before the take it follows waits for it.
    const adopted: Runtime[] = [];
    let skipped = 0;
    for (const t of takes) {
      if (!this.owns(t) || this.rts.has(t.id) || this.left.has(t.id)) continue;
      if (t.status === "done") {
        void store.deleteTake(t.id).catch(() => undefined).then(() => this.recountSoon());
        continue;
      }
      if (t.status === "kept") {
        this.kept.set(t.id, t);
        continue;
      }
      if (!locks.available && t.ownerTab !== tabId && clock.now() - t.aliveAt < OWNER_STALE_MS) {
        skipped++;
        continue;
      }
      const release = await locks.hold(lockName(t.id));
      if (release === null || this.rts.has(t.id) || this.left.has(t.id)) {
        release?.();
        if (release === null) skipped++;
        continue;
      }
      const rt = newRuntime(t, false);
      rt.releaseLock = release;
      const now = clock.now();
      t.ownerTab = tabId;
      t.aliveAt = now;
      rt.finalIn = true;
      // A part stored in the instant before the page died, after its record was last written: count it.
      const seqs = await store.listPartSeqs(t.id).catch(() => [] as number[]);
      if (seqs.length) t.nextSeq = Math.max(t.nextSeq, seqs[seqs.length - 1] + 1);
      if (t.status === "capturing") {
        t.status = "held";
        t.cutShortAt = t.lastAudioAt;
        t.cutReason = "page_closed";
        t.finishAt = autoFinishAt(t.meetingEndAt, t.lastAudioAt);
        // It stopped without Stop being pressed: every finish of it says so (decisions D6).
        t.interruptions.push({ at: iso(t.lastAudioAt), resumed_at: null, reason: "page_closed" });
        this.diag(rt, "cut_short", { reason: "page_closed" });
      }
      this.rts.set(t.id, rt);
      adopted.push(rt);
    }
    for (const rt of adopted) {
      if (rt.rec.status === "held") this.holdOrFinish(rt);
      this.save(rt);
      void this.uploader.pump(rt);
    }
    // (Signed in as someone else meanwhile: that sign-in's own look decides.)
    if (`${this.customerId}\u0000${this.owner}` === who) this.adopted = skipped === 0 && !store.unopened;
    this.changed();
  }

  /** A cut-short take: ask "Carry on?" until its time, then finish it on its own. */
  protected holdOrFinish(rt: Runtime): void {
    const t = rt.rec;
    const at = t.cutShortAt ?? t.lastAudioAt;
    const now = this.deps.clock.now();
    const reason = t.cutReason ?? "page_closed";
    if (t.finishAt !== null && now < t.finishAt) {
      this.offer = this.offerFor(rt, at, reason, t.finishAt);
      this.heldTimers.set(t.id, this.deps.clock.setTimeout(() => {
        if (this.offer?.takeId === t.id) this.offer = { ...this.offer, finishAt: null };
        this.close(rt);
        this.changed();
      }, t.finishAt - now));
    } else {
      this.offer = this.offerFor(rt, at, reason, null);
      this.close(rt);
    }
  }

  /**
   * Corvus answered empty_recording: it holds no audio for the take. Final, but
   * the device's copy is the only one left (integration I1): the take leaves
   * the device only when none of its parts here holds any audio; otherwise it
   * is kept to save as a file ("Corvus found no audio in it").
   */
  protected async noAudio(rt: Runtime): Promise<void> {
    const r = rt.rec;
    let sound = [...rt.blobs.values()].some((b) => b.size > 0);
    if (!sound) {
      try {
        const store = await this.ensureStore();
        for (const seq of await store.listPartSeqs(r.id)) {
          if (((await store.getPart(r.id, seq))?.data.size ?? 0) > 0) {
            sound = true;
            break;
          }
        }
      } catch {
        sound = true; // the device could not say: keep it
      }
    }
    if (!sound) return this.dropped(rt, "empty_recording");
    this.retire(rt);
    r.status = "kept";
    r.noAudio = true;
    this.kept.set(r.id, r);
    const copy: TakeRecord = { ...r, interruptions: r.interruptions.map((i) => ({ ...i })) };
    const queued = (this.saves.get(r.id) ?? Promise.resolve()).then(() => this.ensureStore()).then((s) => s.putTake(copy)).catch(() => undefined);
    this.saves.set(r.id, queued);
    this.diag(rt, "empty_recording_kept");
    this.changed();
  }

  /** Who is signed in now; someone else than before: nothing of the last person's is shown or asked about. */
  protected signInAs(customerId: string, who: { owner: string; family: boolean }): void {
    if (customerId !== this.customerId || who.owner !== this.owner || who.family !== this.family) {
      this.adopted = false;
      this.listed.clear();
      this.clearAfter();
      this.failure = null;
      this.notice = null;
    }
    this.customerId = customerId;
    this.owner = who.owner;
    this.family = who.family;
  }

  /** Back on a page that holds a take of this person's cut short: ask "Carry on?" again (its finish timer still runs). */
  protected askAgain(): void {
    const held = this.mine().filter((rt) => rt.rec.status === "held").sort((a, b) => b.rec.startedAt - a.rec.startedAt)[0];
    if (this.myOffer() || !held?.rec.finishAt) return;
    this.offer = this.offerFor(held, held.rec.cutShortAt ?? held.rec.lastAudioAt, held.rec.cutReason ?? "page_closed", held.rec.finishAt);
    this.changed();
  }

  /** The system gave the microphone back: a mute long enough to matter is a stretch not recorded (D6). */
  protected unmuted(rt: Runtime): void {
    const since = rt.mutedSince;
    rt.mutedSince = null;
    const now = this.deps.clock.now();
    if (since !== null && rt.rec.status === "capturing" && now - since >= MUTE_GAP_MS) {
      rt.rec.interruptions.push({ at: iso(since), resumed_at: iso(now), reason: "microphone_muted" });
      this.save(rt);
      this.diag(rt, "microphone_muted", { seconds: Math.round((now - since) / 1000) });
    }
    this.changed();
  }

  /**
   * Store what the microphone has captured so far (the Mac app, before a quit): ask the
   * recorder for its slice, or wait for a stopping take's last piece (FLUSH_WAIT_MS at most),
   * then for every write. True only when that slice came and everything captured on this page
   * is on the device or on the server: then `flushed` goes up (I3), and the app may quit at
   * once. Never on a timeout, a failed write or a store in memory (the app waits its own 3 s).
   */
  async flush(): Promise<boolean> {
    const end = this.deps.clock.now() + FLUSH_WAIT_MS;
    const rt = this.live;
    let sliceIn = true;
    if (rt?.recorder?.state === "recording" && !rt.stopping) sliceIn = await this.nextPiece(rt, end);
    // A take whose recorder still owes its last piece (Stop was pressed, or the microphone went).
    const owed = [...this.rts.values()].filter((r) => !r.finalIn && r.rec.status !== "done" && (r.stopping || r.rec.status !== "capturing"));
    if (owed.length && !(await this.waitUntil(() => owed.every((r) => r.finalIn), end))) sliceIn = false;
    const takes = [...this.rts.values()].filter((r) => r.rec.status !== "done");
    await Promise.all(takes.flatMap((r) => [...r.stored.values()]));
    await Promise.all(takes.map((r) => this.saves.get(r.rec.id)));
    const onDevice = this.storeRef?.kind === "device" && takes.every((r) => r.unstored.size === 0 && !this.recordFailed.has(r.rec.id));
    if (!sliceIn || !onDevice) return false;
    this.flushes++;
    this.changed();
    return true;
  }

  /** The take's next piece (asked for now); false when none came by `end`. */
  protected nextPiece(rt: Runtime, end: number): Promise<boolean> {
    const { clock } = this.deps;
    return new Promise((resolve) => {
      const w = {
        rt,
        fn: () => {
          clock.clearTimeout(t);
          this.pieceWaiters.delete(w);
          resolve(true);
        },
      };
      const t = clock.setTimeout(() => {
        this.pieceWaiters.delete(w);
        resolve(false);
      }, Math.max(0, end - clock.now()));
      this.pieceWaiters.add(w);
      try {
        rt.recorder!.requestData();
      } catch {
        clock.clearTimeout(t);
        this.pieceWaiters.delete(w);
        resolve(false); // no slice on request: the next timeslice brings it
      }
    });
  }

  /** `cond` comes true by `end` (looked at every 100 ms). */
  protected waitUntil(cond: () => boolean, end: number): Promise<boolean> {
    const { clock } = this.deps;
    return new Promise((resolve) => {
      const look = () => {
        if (cond()) resolve(true);
        else if (clock.now() >= end) resolve(false);
        else clock.setTimeout(look, 100);
      };
      look();
    });
  }

  /** A piece of `rt` came (its write is under way): a flush waiting for its slice has it. */
  protected pieceIn(rt: Runtime): void {
    for (const w of [...this.pieceWaiters]) if (w.rt === rt) w.fn();
  }

  /** A call recording whose share ended: share the call again into it. Call straight from the tap. */
  async reshareCallSound(): Promise<"ok" | "cancelled" | "no_sound" | "blocked" | "none"> {
    const cs = this.live?.stream?.callSound;
    if (!cs) return "none";
    const got = await cs.reshare();
    this.changed();
    return got;
  }

  /** A call recording that loses one side carries on with the other (the screen says which); both gone end its track. */
  protected watchCallSound(rt: Runtime, stream: StreamLike): void {
    stream.callSound?.onChange(() => {
      if (this.live !== rt) return;
      this.diag(rt, "call_sound", { reason: stream.callSound?.state() ?? null });
      this.changed();
    });
  }

  /** The microphone (or the call's share) was refused at a start: a share cancelled or without sound is a quiet notice (Start asks again), the rest a failure. */
  protected refused(e: Error, quiet: boolean, callSound: boolean): void {
    const share = callSound ? callStartError(e) : null;
    if (share && "notice" in share) this.notice = share.notice;
    else if (!quiet) this.failure = share?.failure ?? micErrorMessage(e);
    this.changed();
  }
}
